import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Rocket, Sparkles, Shuffle, Trash2, Upload, Wand2 } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { apiClient } from "../../lib/api";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";
import { ImageLightbox } from "../ImageLightbox";
import { ColorChartBuilder, HexCopy, inkHex } from "./ColorChartBuilder";
import { MockupChooserModal } from "../mockup/MockupChooserModal";
import { TextTemplateEditor, type TextTemplate } from "./TextTemplateEditor";
import { CustomAutoModal, CustomAutoJobRow, useCustomAutoJobs } from "./CustomAutopilot";
import { DesignAreaEditor, type AreaRect } from "../mockup/DesignAreaEditor";
import type { CustomMockup } from "@erc/shared";
import { checkOptionCombo, PRODUCT_COLOR_KEY, type OptionRule } from "@erc/shared";
import { OptionRulesEditor, type RuleField } from "./OptionRulesEditor";
import { DesignDetailModal, buildMockupStudioUrl } from "../../pages/DesignsPage";
import type { Design } from "@erc/shared";

// Custom (kisisellestirilebilir) ilham listesi icin:
// 1) AI haritasi: ne kisisellestiriliyor, ornek degerler, urun sayisi
// 2) Strateji: degerleri DEGISTIR ya da (satis getiriyorsa) YERLERINI degistir
// 3) Secilen degerle BIZIM tasarimimiz
// 4) Ilham listesinin ilk gorselinden ilham alan KOLAJ mockup

interface CustomizationField {
  key: string; labelTr: string; kind: string; exampleValues: string[];
  pattern?: string; casing?: string; maxChar?: number; personalizationLabel?: string;
  /** text: yazi basilir · graphic: menuden illustrasyon · color: menuden renk · note: serbest istek */
  role?: "text" | "graphic" | "color" | "note";
  options?: Array<{ code?: string; label: string; hex?: string }>;
  target?: string;
}
const ROLE_TR: Record<string, string> = { text: "Yazı", graphic: "Menüden görsel", color: "Menüden renk", note: "Serbest istek (not)" };
const isChoice = (f?: CustomizationField) => !!f && (f.role === "graphic" || f.role === "color");
export interface CustomizationMap {
  isCustom: boolean; confidence: number; summaryTr: string;
  fields: CustomizationField[]; fixedText: string[];
  mockup: { itemCount: number; items: Array<{ text: string; productColor?: string; textColor?: string; textHex?: string; fieldKeys?: string[]; placement?: string; values?: Record<string, string> }>; sceneTr?: string };
  colorCharts?: Array<{ url: string; analysisDesignId: string; createdAt: string }>;
  /** Tasarim x renk kartelasinin renk listesi (kullanici ekledi/duzenledi) */
  chartColors?: Array<{ name: string; hex?: string }>;
  designInk?: { name: string; hex: string } | null;
  blankScene?: { url: string; sourceUrl: string; model: string; createdAt: string; mockupId?: string };
  alternatives: Record<string, string[]>; chosen: Record<string, string>;
  strategy: "replace" | "reorder"; strategyReasonTr?: string;
  collages?: Array<{
    url: string; analysisDesignId: string; values: string[]; createdAt: string;
    items?: Array<{ value: string; productColor?: string; textColor?: string; textHex?: string }>;
    model?: string; extraPrompt?: string;
  }>;
  colorOptions?: Array<{ name: string; hex?: string }>;
  imageUrls?: string[];
  /** Duzenlenebilir yazi sablonlari: analysisDesignId -> sablon */
  textTemplates?: Record<string, TextTemplate>;
  /** Secenek kurallari (olmayacak kombinasyonlar) */
  optionRules?: OptionRule[];
}

/** Kolaj satirinin yazi (murekkep) rengi icin kuraldaki sanal alan */
const INK_KEY = "__ink";
const uniq = (a: Array<string | undefined>) => [...new Set(a.map((x) => (x ?? "").trim()).filter(Boolean))];

/** Kural duzenleyicisinin alanlari: urun rengi, yazi rengi, secenekli alanlar */
function ruleFields(map: CustomizationMap, planColors: string[], planInks: string[]): RuleField[] {
  const out: RuleField[] = [];
  const products = uniq([...planColors, ...map.mockup.items.map((i) => i.productColor)]);
  if (products.length) out.push({ key: PRODUCT_COLOR_KEY, label: "Ürün rengi", labelEn: "color", values: products });
  const inks = uniq([...planInks, ...(map.chartColors ?? []).map((c) => c.name)]);
  if (inks.length) out.push({ key: INK_KEY, label: "Yazı rengi", labelEn: "text color", values: inks });
  for (const f of map.fields) {
    if (!isChoice(f) || !f.options?.length) continue;
    out.push({ key: f.key, label: f.labelTr, labelEn: (f.personalizationLabel || f.kind || f.key).replace(/_/g, " ").toLowerCase(), values: f.options.map((o) => o.label) });
  }
  return out;
}

/** Kolaj satiri: kural ihlalleri + renk kontrasti */
function rowIssues(map: CustomizationMap, valueKey: string, it: { value: string; productColor?: string; textColor?: string; textHex?: string; values?: Record<string, string> }) {
  const colors: Array<{ key: string; label: string; hex?: string }> = [];
  if (it.textHex) colors.push({ key: INK_KEY, label: "Yazı rengi", hex: it.textHex });
  for (const f of map.fields) {
    if (f.role !== "color" || !it.values?.[f.key]) continue;
    colors.push({ key: f.key, label: f.labelTr, hex: f.options?.find((o) => o.label === it.values![f.key])?.hex });
  }
  return checkOptionCombo({
    values: { ...(it.values ?? {}), [valueKey]: it.value, [INK_KEY]: it.textColor ?? "" },
    colors,
    product: it.productColor ? { name: it.productColor, hex: inkHex(it.productColor) } : undefined,
    rules: map.optionRules,
    labels: Object.fromEntries([...map.fields.map((f) => [f.key, f.labelTr]), [INK_KEY, "Yazı rengi"]]),
  });
}

const PLACEMENT_TR: Record<string, string> = { front: "Ön", back: "Arka", sleeve: "Kol", pocket: "Cep" };
const escRe = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
type MapItem = CustomizationMap["mockup"]["items"][number];
/** Baskida hangi alanlar var: analizden, yoksa ornek degerlerin yazida gecmesinden */
function itemKeys(map: CustomizationMap, it: MapItem): string[] {
  const valid = map.fields.map((f) => f.key);
  const given = (it.fieldKeys ?? []).filter((k) => valid.includes(k));
  if (given.length) return given;
  const t = it.text ?? "";
  return map.fields
    .filter((f) => f.exampleValues.some((v) => v.trim() && new RegExp(`(^|[^\\p{L}\\p{N}])${escRe(v.trim())}($|[^\\p{L}\\p{N}])`, "iu").test(t)))
    .map((f) => f.key);
}
type PrintGroup = { id: string; keys: string[]; label: string; sample: string };
/** Farkli baskilar (on: maskot, arka: isim + numara). Tek tur baski varsa bos. */
function printGroups(map: CustomizationMap): PrintGroup[] {
  const out: PrintGroup[] = [];
  map.mockup.items.forEach((it) => {
    const keys = itemKeys(map, it);
    if (!keys.length) return;
    const id = [...keys].sort().join(",");
    if (out.some((g) => g.id === id)) return;
    out.push({ id, keys, sample: it.text, label: PLACEMENT_TR[it.placement ?? ""] ?? `Baskı ${out.length + 1}` });
  });
  return out.length > 1 ? out : [];
}
const groupValue = (map: CustomizationMap, keys: string[]) => keys.map((k) => map.chosen[k]).filter(Boolean).join(" ");
function parseDesignChosen(prompt?: string): Record<string, string> | null {
  const json = prompt?.match(/^CUSTOM \([^)]*\) (\{[^}]*\})/)?.[1];
  try { return json ? JSON.parse(json) as Record<string, string> : null; } catch { return null; }
}

/** Ilham gorselinden algilanan yazi (murekkep) rengi */
function detectedInk(map: CustomizationMap): { name: string; hex: string } | null {
  for (const it of map.mockup.items) {
    const hex = it.textHex || (it.textColor ? inkHex(it.textColor) : undefined);
    if (hex) return { name: it.textColor || hex, hex };
  }
  return null;
}
/** Uretimde kullanilacak yazi rengi: kullanici secimi, yoksa algilanan */
function effectiveInk(map: CustomizationMap): { name: string; hex: string } | null {
  return map.designInk === undefined ? detectedInk(map) : map.designInk;
}

const KIND_TR: Record<string, string> = {
  person_name: "Kişi adı", first_name: "Ad", last_name: "Soyad", teacher_name: "Öğretmen adı",
  family_name: "Aile adı", team_mascot: "Takım / maskot", school_name: "Okul", animal_name: "Hayvan adı",
  pet_name: "Evcil hayvan adı", city: "Şehir", date: "Tarih", year: "Yıl", number: "Sayı",
  initials: "Baş harfler", word: "Kelime", photo: "Fotoğraf", other: "Diğer",
};

const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);

async function api<T>(path: string, method: string, body?: unknown): Promise<T> {
  // DELETE govdeli istekler de desteklenir (kolaj silme)
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, {
    method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

export function CustomizationPanel({ analysisId, initial, sourceImages, productTypeKey }: {
  analysisId: string;
  /** Urun tipi: otomatik listede varsayilan sablon buna gore */
  productTypeKey?: string | null;
  initial: CustomizationMap | null;
  /** Ilham listesinin gorselleri — analize hangilerinin gidecegi secilir */
  sourceImages: string[];
}) {
  const qc = useQueryClient();
  const [map, setMap] = useState<CustomizationMap | null>(initial);
  useEffect(() => { setMap(initial); }, [initial]);
  const [busy, setBusy] = useState<null | "analyze" | "design" | "mockup">(null);
  const [chartSaving, setChartSaving] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  /** Bos sahne (baskisi silinmis ilham fotografi) yeniden uretilsin mi */
  const [regenerateBlank, setRegenerateBlank] = useState(false);
  /** Baski alanlarini ilham gorselinden yeniden olc */
  const [redetectAreas, setRedetectAreas] = useState(false);
  /** Bos sahneyi mockuplardan sec */
  const [pickBlank, setPickBlank] = useState(false);
  /** Kodla basilmis yerlesim onizlemesi (AI yok) */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  /** Son adim: AI ile kumasa oturt ya da hic (birebir) */
  const [finalStep, setFinalStep] = useState<"ai" | "none">("ai");
  /** Cok alanli mockup secimi (tasarimlari alanlara yerlestir) */
  const [pickPlaceMockup, setPickPlaceMockup] = useState(false);
  /** Disaridan tasarim ekleme formu */
  const [upload, setUpload] = useState<{ value: string } | null>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const uploadDesign = (file: File | undefined) => {
    if (!file || !upload?.value.trim()) return;
    const r = new FileReader();
    r.onload = () => void run("design", async () => {
      const d = await api<{ id: string }>(`/${analysisId}/customization/upload`, "POST", { image: String(r.result), value: upload.value.trim() });
      setUpload(null);
      setDesignId(d.id);
      await qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
      void refetchVariants();
      setInfo("Tasarım eklendi — kolajda kullanılmak üzere seçildi.");
    });
    r.readAsDataURL(file);
  };
  /** Plan satiri urunleri: uretiliyor olanlar */
  const [itemBusy, setItemBusy] = useState<Set<number>>(new Set());
  const produceItem = async (i: number) => {
    const it = plan[i];
    if (!it?.value.trim() || !designId) return;
    setItemBusy((s0) => new Set(s0).add(i)); setError(null);
    try {
      const d = await api<{ id: string }>(`/${analysisId}/customization/item-design`, "POST",
        { analysisDesignId: designId, value: it.value, textHex: it.textHex || undefined, textColor: it.textColor || undefined, model, values: it.values });
      setPlan((p) => p.map((x, j) => (j === i ? { ...x, designId: d.id } : x)));
      void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
      void refetchVariants();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setItemBusy((s0) => { const n = new Set(s0); n.delete(i); return n; }); }
  };
  /** Bos sahne mockup'inin alan duzenleyicisi */
  const [areaEdit, setAreaEdit] = useState<CustomMockup | null>(null);
  const openAreaEditor = async () => {
    try { setAreaEdit(await api<CustomMockup>(`/${analysisId}/customization/blank-mockup`, "GET")); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  /** Revize: hangi kolaj/tasarim ve ek talimat */
  const [revise, setRevise] = useState<{ kind: "collage" | "design"; key: string; prompt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newAlt, setNewAlt] = useState("");
  const [lightbox, setLightbox] = useState<number | null>(null);
  /** Tasarimi buyuk gor (yaziyi kontrol etmek icin) */
  const [zoom, setZoom] = useState<string | null>(null);
  const { models, model, setModel } = useDesignModel("erc:design-model:custom");
  // Analize gidecek gorseller: onceki secim, yoksa yalniz ilk gorsel
  const [picked, setPicked] = useState<string[]>(() =>
    initial?.imageUrls?.length ? initial.imageUrls.filter((u) => sourceImages.includes(u)) : sourceImages.slice(0, 1));
  const togglePick = (u: string) => setPicked((p) =>
    p.includes(u) ? p.filter((x) => x !== u) : p.length >= 6 ? p : sourceImages.filter((x) => x === u || p.includes(x)));

  // Bu analizin CUSTOM tasarimlari (kolaj icin secilir)
  const { data: designs = [] } = useQuery({
    queryKey: ["analysis-designs", analysisId],
    queryFn: () => apiClient.listAnalysisDesigns(analysisId),
    refetchInterval: (q) => ((q.state.data as Array<{ status: string }> | undefined)?.some((d) => d.status === "generating") ? 3000 : false),
  });
  const customDesigns = (designs as Array<{ id: string; sku?: string | null; prompt?: string; status: string; imageUrl?: string | null; error?: string | null; model?: string | null }>)
    .filter((d) => d.prompt?.startsWith("CUSTOM ("));
  const [designId, setDesignId] = useState<string>("");
  // Custom tasarimlarin koyu varyantlari (kolajda koyu tisortlere basilir)
  const { data: variants = {}, refetch: refetchVariants } = useQuery({
    queryKey: ["custom-variants", analysisId, customDesigns.length],
    queryFn: () => api<Record<string, { variantUrl: string | null; theme: string; designId: string }>>(`/${analysisId}/customization/variants`, "GET"),
  });
  const [info, setInfo] = useState<string | null>(null);
  /** Otomatik liste hatti: baslatma modali + bu analizin son isi */
  const [autoOpen, setAutoOpen] = useState(false);
  const { data: autoJobs = [] } = useCustomAutoJobs(analysisId);
  // Hat bir adim ilerledikce uretilenler (harita, tasarimlar, kolaj) panele yansisin
  const autoProgress = autoJobs[0] ? `${autoJobs[0].id}:${autoJobs[0].status}:${autoJobs[0].step}` : "";
  useEffect(() => {
    if (!autoProgress) return;
    void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
    apiClient.getAnalysis(analysisId)
      .then((a) => { const m = (a as unknown as { customization?: CustomizationMap | null }).customization; if (m) setMap(m); })
      .catch(() => { /* panel eski haliyle kalir */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoProgress]);
  /** Duzenlenebilir sablon editoru acik olan tasarim */
  const [tplEdit, setTplEdit] = useState<string | null>(null);
  /** Tasarim duzenleme modali (Tasarimlar sayfasindakiyle ayni) */
  const [detail, setDetail] = useState<Design | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const openDetail = async (analysisDesignId: string) => {
    const poolId = variants[analysisDesignId]?.designId;
    if (!poolId) { setError("Tasarım havuzda bulunamadı (henüz kaydedilmemiş olabilir)"); return; }
    try { setDetail(await apiClient.getDesign(poolId)); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  /** Koyu varyanti kullanici YUKLER (dosyadan) */
  const uploadDark = (analysisDesignId: string, file: File | undefined) => {
    const poolId = variants[analysisDesignId]?.designId;
    if (!file || !poolId) return;
    const r = new FileReader();
    r.onload = () => void run("design", async () => {
      const res = await fetch(`${getBackendUrl()}/api/designs/${poolId}/theme-variant`, {
        // Yuklenen = KOYU tisort versiyonu; ana tasarim acik tisort icin
        method: "POST", headers: authHeaders(), body: JSON.stringify({ theme: "light", variantImage: String(r.result) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Yüklenemedi");
      await refetchVariants();
      setInfo("Koyu varyant yüklendi — kolajda koyu tişörtlere, listelerde koyu mockuplara basılır.");
    });
    r.readAsDataURL(file);
  };
  useEffect(() => {
    if (!designId) {
      const first = customDesigns.find((d) => d.status === "completed" && d.imageUrl);
      if (first) setDesignId(first.id);
    }
  }, [customDesigns, designId]);

  async function run<T>(kind: "analyze" | "design" | "mockup", fn: () => Promise<T>) {
    setBusy(kind); setError(null);
    try { return await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); return null; } finally { setBusy(null); }
  }

  /** Custom listeyi tasi: tasarim + kolajlar + kartela hazirlanir */
  const [sending, setSending] = useState<null | "review" | "studio" | "draft">(null);
  const prepareListing = async () => {
    if (!designId) throw new Error("Önce listenin tasarımını seçin (Kolajda kullan)");
    return api<{ designId: string; collages: string[]; extras: string[]; designPool?: Array<{ label: string; lightUrl: string | null; darkUrl: string | null }> }>(`/${analysisId}/customization/prepare-listing`, "POST",
      { analysisDesignId: designId, items: plan });
  };
  const sendTo = async (target: "review" | "studio" | "draft") => {
    setSending(target); setError(null);
    try {
      const prep = await prepareListing();
      if (target === "review") {
        window.open(`/listing-review?designIds=${prep.designId}`, "_blank");
      } else if (target === "studio") {
        const d = await apiClient.getDesign(prep.designId);
        const images = [
          ...prep.collages.map((u, i) => ({ url: u, label: i === 0 ? "Kolaj (ana görsel)" : `Kolaj ${i + 1}` })),
          ...prep.extras.map((u) => ({ url: u, label: "Kartela / ek görsel" })),
        ];
        try {
          localStorage.setItem("erc:studio-import", JSON.stringify({ designId: prep.designId, images }));
          // Coklu tasarim havuzu: Studio mockuplara dagitir (sayfa yenilense de kalir)
          if ((prep.designPool?.length ?? 0) > 1) localStorage.setItem(`erc:studio-pool:${prep.designId}`, JSON.stringify(prep.designPool));
        } catch { /* dolu */ }
        window.open(buildMockupStudioUrl(d), "_blank");
      } else {
        const r = await fetch(`${getBackendUrl()}/api/listing-drafts/from-design`, {
          method: "POST", headers: authHeaders(), body: JSON.stringify({ designId: prep.designId }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(`${j.error || j.message || "Taslak oluşturulamadı"} — şablon/mockup grubu eksikse Liste Denetimi'nden tamamlayın.`);
        setInfo(`Taslak oluşturuldu: "${j.data?.title ?? ""}" (${j.data?.images ?? 0} görsel) — Listing Builder'da.`);
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSending(null); }
  };

  const deleteCollage = async (url: string) => {
    if (!confirm("Bu kolaj silinsin mi? (Listedeki ana görsel bir önceki kolaja düşer)")) return;
    try {
      await api(`/${analysisId}/customization/collage`, "DELETE", { url });
      setMap((m) => (m ? { ...m, collages: (m.collages ?? []).filter((c) => c.url !== url) } : m));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const save = (patch: Partial<CustomizationMap>) => {
    if (!map) return;
    setMap({ ...map, ...patch });
    void api<CustomizationMap>(`/${analysisId}/customization`, "PATCH", patch).catch((e) => setError(String(e.message ?? e)));
  };

  // Gorsel modeli secimi: tasarim, yeniden uretim ve kolaj dugmelerinin yaninda
  const modelSelect = (
    <select value={model} onChange={(e) => setModel(e.target.value)}
      className="max-w-[220px] rounded border border-gray-200 bg-white px-2 py-1.5 text-[11px]" title="Görsel üretim modeli">
      {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
    </select>
  );
  const modelName = (id?: string | null) => models.find((m) => m.id === id)?.name ?? id ?? "";

  const field = map?.fields[0];
  const alts = field ? map!.alternatives[field.key] ?? [] : [];
  const groups = map ? printGroups(map) : [];
  const sameKeys = (a: string[], b: string[]) => [...a].sort().join(",") === [...b].sort().join(",");
  // ── Tasarim ayarlari (urune gore): ornament sekli, zemin, isim sayisi ─────────
  const isOrnament = productTypeKey === "ornament";
  const [shapeOpt, setShapeOpt] = useState<"round" | "heart" | "any">(() => {
    try { const v = localStorage.getItem("erc:custom:shape"); return v === "heart" || v === "any" ? v : "round"; } catch { return "round"; }
  });
  const [bgOpt, setBgOpt] = useState<"transparent" | "full">(() => {
    try { return localStorage.getItem("erc:custom:bg") === "full" ? "full" : "transparent"; } catch { return "transparent"; }
  });
  const remember = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* depolama kapali */ } };
  // Cok isimli alanlar (ornekte bosluk/virgulle ayrilmis 2+ isim): kac isimle tasarim
  // (sunucudaki sampleNamesOf ile ayni kural: tek degerde birden fazla isim ya da tek urunde birden fazla ornek)
  const multiFields = (map?.fields ?? []).map((f) => {
    // Yalniz isim alanlari (baslik/slogan bolunmesin)
    if (!/name|isim|üye|uye|member|family|aile/i.test(`${f.kind} ${f.key} ${f.labelTr}`)) return null;
    const nameRe = /^[A-Z][a-zA-Z'-]+$/;
    const toks = (f.exampleValues?.[0] ?? "").split(/\s*(?:,|&|\/|\+|\band\b)\s*|\s+/).filter(Boolean);
    const ex = (f.exampleValues ?? []).map((v) => v.trim()).filter(Boolean);
    const n = toks.length >= 2 && toks.every((t) => nameRe.test(t)) ? toks.length
      : map?.mockup.itemCount === 1 && ex.length >= 2 && ex.every((v) => nameRe.test(v)) ? ex.length : 0;
    return n ? { key: f.key, label: f.labelTr, sample: n } : null;
  }).filter((x): x is { key: string; label: string; sample: number } => !!x);
  const [nameCounts, setNameCounts] = useState<Record<string, number>>({});
  /** Tasarim istegine eklenen ayarlar */
  const designOpts = () => ({
    ...(isOrnament ? { printShape: shapeOpt, background: shapeOpt === "any" ? "transparent" : bgOpt } : {}),
    ...(Object.keys(nameCounts).length ? { nameCounts } : {}),
  });

  /** Cok baskili urunde tasarim istegi: yalnizca o baskinin alanlari */
  const groupArgs = (keys: string[]) => {
    const g = groups.find((x) => sameKeys(x.keys, keys));
    return g ? { fieldKeys: g.keys, printOf: g.sample, otherPrints: groups.filter((x) => x !== g).map((x) => x.sample) } : {};
  };

  // Kolaja yazilacak degerler (sunucudaki collageValues ile ayni kural)
  const preview = (() => {
    if (!map || !field) return [] as string[];
    const n = Math.max(1, map.mockup.itemCount);
    if (map.strategy === "reorder") {
      const src = field.exampleValues.length ? field.exampleValues : map.mockup.items.map((i) => i.text);
      return src.length ? Array.from({ length: n }, (_, i) => src[(i + 1) % src.length]) : [];
    }
    const first = map.chosen[field.key];
    return [first, ...alts.filter((v) => v !== first)].filter(Boolean).slice(0, n);
  })();

  // Kolaj plani: her urunun rengi + yazisi. Uretmeden once gosterilir ve
  // duzenlenebilir; secilen deger/strateji degisince yeniden kurulur.
  // Yazi rengi urunle birlikte gelir (ilham gorselinde her urunun baski rengi farkli)
  const [plan, setPlan] = useState<Array<{ value: string; productColor: string; textColor: string; textHex: string; designId?: string; keys?: string[];
    /** Secenekli urun: satirin diger secimleri (serit rengi vb.) */
    values?: Record<string, string> }>>([]);
  const planKey = `${preview.join("|")}#${map?.mockup.items.map((i) => `${i.productColor}/${i.textHex}`).join("|")}#${groups.length > 1 || map?.fields.some(isChoice) ? JSON.stringify(map?.chosen) : ""}`;
  useEffect(() => {
    if (!map || !field) { setPlan([]); return; }
    const items = map.mockup.items;
    const n = Math.max(1, map.mockup.itemCount);
    if (groups.length > 1) {
      // On/arka gibi farkli baskilar: her alan KENDI tasarimini alir
      setPlan(Array.from({ length: n }, (_, i) => {
        const it = items[i];
        const keys = it ? itemKeys(map, it) : [];
        return {
          value: groupValue(map, keys) || it?.text || "", keys,
          productColor: it?.productColor ?? "", textColor: it?.textColor ?? "", textHex: it?.textHex ?? "",
        };
      }));
      return;
    }
    // Tasarim = isim + baski rengi IKILISI. Ilham gorselindeki eslesme
    // tekrarlanmasin: ikililer urunlere BIR KAYDIRILARAK dagitilir.
    const values0 = map.strategy === "reorder"
      ? (field.exampleValues.length ? field.exampleValues : items.map((x) => x.text).filter(Boolean))
      : [map.chosen[field.key], ...alts.filter((v) => v !== map.chosen[field.key])].filter(Boolean);
    if (values0.length === 0) { setPlan([]); return; }
    // Secenekli urun: diger secenekli alanlar da urunden urune degisir (secilen + alternatifler)
    const others = map.fields.filter((f) => f.key !== field.key && isChoice(f));
    const pairs = Array.from({ length: n }, (_, j) => ({
      value: values0[j % values0.length],
      textColor: items[j]?.textColor ?? "",
      textHex: items[j]?.textHex ?? "",
      ...(others.length ? { values: Object.fromEntries(others.map((f) => {
        const list = [map.chosen[f.key], ...(map.alternatives[f.key] ?? []).filter((v) => v !== map.chosen[f.key])].filter(Boolean);
        return [f.key, list.length ? list[j % list.length] : ""];
      })) } : {}),
    }));
    setPlan(Array.from({ length: n }, (_, i) => ({
      ...pairs[n > 1 ? (i + 1) % n : i],
      productColor: items[i]?.productColor ?? "",
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);
  // Isim eslesen, DAHA ONCE uretilmis tasarim satira kendiliginden atanir
  // (ilk tasarim yeniden uretilmez). Baski rengi eslesen urun tasarimi oncelikli.
  const valueOfDesign = (d: { prompt?: string }) => d.prompt?.match(/^CUSTOM \(([^)]*)\)/)?.[1]?.split(", ")[0]?.trim() ?? "";
  useEffect(() => {
    const ready = customDesigns.filter((d) => d.status === "completed" && d.imageUrl);
    if (ready.length === 0) return;
    setPlan((p) => {
      let changed = false;
      const next = p.map((row) => {
        if (row.designId || !row.value.trim()) return row;
        if (row.keys?.length && groups.length > 1) {
          // Ayni baskinin (ayni alanlar + ayni degerler) en yeni tasarimi
          const hit = ready.find((d) => {
            const c = parseDesignChosen(d.prompt);
            return c && sameKeys(Object.keys(c), row.keys!) && row.keys!.map((k) => c[k]).join(" ").toLowerCase() === row.value.trim().toLowerCase();
          });
          if (!hit) return row;
          changed = true;
          return { ...row, designId: hit.id };
        }
        const same = ready.filter((d) => designFieldValue(d).toLowerCase() === row.value.trim().toLowerCase());
        if (same.length === 0) return row;
        const colorMatch = row.textHex
          ? same.find((d) => d.prompt?.includes(row.textColor || row.textHex))
          : same.find((d) => !d.prompt?.includes("URUN TASARIMI ·"));
        changed = true;
        return { ...row, designId: (colorMatch ?? same[0]).id };
      });
      return changed ? next : p;
    });
  }, [customDesigns.map((d) => `${d.id}:${d.status}`).join(","), planKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Satira dosyadan tasarim yukle (satirin ismiyle kaydedilir ve atanir) */
  const rowUploadRef = useRef<HTMLInputElement>(null);
  const [rowUploadIdx, setRowUploadIdx] = useState<number | null>(null);
  const uploadForRow = (file: File | undefined) => {
    const i = rowUploadIdx;
    if (!file || i === null) return;
    const value = plan[i]?.value.trim();
    if (!value) return;
    const r = new FileReader();
    r.onload = () => void (async () => {
      setItemBusy((s0) => new Set(s0).add(i));
      try {
        const d = await api<{ id: string }>(`/${analysisId}/customization/upload`, "POST", { image: String(r.result), value });
        setPlan((p) => p.map((x, j) => (j === i ? { ...x, designId: d.id } : x)));
        await qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
        void refetchVariants();
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
      finally { setItemBusy((s0) => { const n = new Set(s0); n.delete(i); return n; }); setRowUploadIdx(null); }
    })();
    r.readAsDataURL(file);
  };

  /** Tasarimin kendi degeri (cok alanli haritada dogru alan) */
  const designFieldValue = (d?: { prompt?: string }) => {
    if (!d) return "";
    const json = d.prompt?.match(/^CUSTOM \([^)]*\) (\{[^}]*\})/)?.[1];
    try { const v = json && field ? (JSON.parse(json) as Record<string, string>)[field.key] : ""; if (v) return v; } catch { /* eski kayit */ }
    return valueOfDesign(d ?? {});
  };
  /** Atanmamis ve tasarimdaki isimden farkli satirlar = AI ile uretilecek isim tasarimi */
  const pendingGenerations = () => {
    const base = designFieldValue(customDesigns.find((d) => d.id === designId)).trim().toLowerCase();
    return plan.filter((r) => r.value.trim() && !r.designId && r.value.trim().toLowerCase() !== base).length;
  };
  /** Uretim olacaksa once sor; iptal edilirse false */
  const confirmGenerations = (action: string) => {
    const n = pendingGenerations();
    if (n === 0) return true;
    return confirm(`${action}: ${n} satıra tasarım atanmamış — her biri için isim tasarımı AI ile üretilecek (~$0.04–0.07/adet). Devam edilsin mi?\n\nÜcret ödemeden yapmak için İptal'e basıp satırlara tasarım atayın ya da yükleyin.`);
  };

  /** Tasarimi (isim + baski rengi) baska urune tasi; urun sutunu sabit kalir */
  const swapRows = (a: number, b: number) => setPlan((p) => {
    if (b < 0 || b >= p.length) return p;
    const next = p.map((x) => ({ ...x }));
    const pick = (x: typeof p[number]) => ({ value: x.value, textColor: x.textColor, textHex: x.textHex, designId: x.designId });
    const A = pick(p[a]), B = pick(p[b]);
    Object.assign(next[a], B); Object.assign(next[b], A);
    return next;
  });
  /** Tum dagilimi bir kaydir */
  const rotatePlan = () => setPlan((p) => p.length < 2 ? p : p.map((x, i) => {
    const src = p[(i + 1) % p.length];
    return { ...x, value: src.value, textColor: src.textColor, textHex: src.textHex, designId: src.designId };
  }));
  const selectedDesign = customDesigns.find((d) => d.id === designId);
  /** Kartela icin olusturulan tasarimlar: etiket = baski rengi (varsa) ya da isim */
  const chartDesigns = customDesigns
    .filter((d) => d.status === "completed" && d.imageUrl)
    .map((d) => {
      const color = d.prompt?.match(/URUN TASARIMI · ([^(]+?) \(/)?.[1]?.trim();
      const value = d.prompt?.match(/^CUSTOM \(([^)]*)\)/)?.[1]?.split(", ")[0]?.trim() ?? "";
      return { id: d.id, url: abs(d.imageUrl), label: (color || value || d.sku || "").toUpperCase() };
    });

  return (
    <section className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/30 p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-fuchsia-800">
          <Sparkles className="h-4 w-4" /> Custom Tasarım
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoOpen(true)}
            title="Tasarım → şablon → isim tasarımları → kolaj → mockup → taslak, sunucuda otomatik"
            className="flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-700">
            <Rocket className="h-3 w-3" /> Otomatik liste
          </button>
          <button
            onClick={() => void run("analyze", async () => setMap(await api<CustomizationMap>(`/${analysisId}/customization/analyze`, "POST", { imageUrls: picked })))}
            disabled={busy !== null || picked.length === 0}
            className="flex items-center gap-1 rounded-lg border border-fuchsia-300 bg-white px-3 py-1 text-xs font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50"
          >
            {busy === "analyze" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            {map ? "Yeniden analiz et" : "Kişiselleştirmeyi analiz et"}
          </button>
        </div>
      </div>

      {autoJobs[0] && (
        <div className="mb-2 rounded-lg border border-violet-200 bg-white"><CustomAutoJobRow j={autoJobs[0]} showTitle={false} /></div>
      )}
      {autoOpen && (
        <CustomAutoModal analysisIds={[analysisId]} productTypeKey={productTypeKey} onClose={() => setAutoOpen(false)}
          onDone={(msg) => { setAutoOpen(false); setInfo(msg); }} />
      )}
      {error && <p className="mb-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700">{error}</p>}
      {info && <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">{info}</p>}

      {/* Analize gidecek gorseller: ana gorsel + renk kartelasi, beden tablosu... */}
      {sourceImages.length > 1 && (
        <div className="mb-3">
          <p className="mb-1 text-[11px] text-gray-500">
            Analize gidecek görseller ({picked.length} seçili, en fazla 6) — ana görsel + renk kartelası, beden tablosu gibi bilgi içerenleri seç:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sourceImages.map((u, i) => (
              <button key={u} onClick={() => togglePick(u)}
                className={`relative h-16 w-16 overflow-hidden rounded border-2 ${picked.includes(u) ? "border-fuchsia-500" : "border-gray-200 opacity-60 hover:opacity-100"}`}>
                <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                <span className={`absolute left-0.5 top-0.5 rounded-full px-1 text-[9px] font-bold text-white ${picked.includes(u) ? "bg-fuchsia-600" : "bg-black/45"}`}>
                  {picked.includes(u) ? "✓" : i + 1}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!map && (
        <p className="text-xs text-gray-500">
          AI ilk görsele, başlığa, açıklamaya ve kişiselleştirme alanlarına bakıp örnek isimleri (MS.TAYLOR, TIGERS…) ve
          türlerini tespit eder; alternatif isimle tasarım ve ilk görselden ilham alan kolaj mockup üretilir.
        </p>
      )}

      {map && !map.isCustom && (
        <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">
          AI bu listede kişiselleştirilen bir yazı bulamadı. {map.summaryTr}
        </p>
      )}

      {map && map.isCustom && field && (
        <div className="space-y-3 text-xs">
          <p className="text-gray-700">{map.summaryTr}</p>

          {/* Tespit */}
          <div className="grid grid-cols-2 gap-2">
            {map.fields.map((f) => (
              <div key={f.key} className="rounded-lg border border-gray-200 bg-white p-2">
                <p className="font-medium text-gray-800">
                  {f.labelTr} <span className="text-gray-400">· {f.role && f.role !== "text" ? ROLE_TR[f.role] : KIND_TR[f.kind] ?? f.kind}</span>
                </p>
                {f.target && f.role !== "text" && <p className="text-[11px] text-gray-400">Etkilediği öğe: {f.target}</p>}
                {f.role === "note"
                  ? <p className="mt-0.5 text-gray-500">Tasarıma otomatik basılmaz — siparişte not olarak gelir.</p>
                  : <p className="mt-0.5 text-gray-600">Örnek: {f.exampleValues.map((v) => <b key={v} className="mr-1">{v}</b>)}</p>}
                {(f.options?.length ?? 0) > 0 && (
                  <p className="mt-1 flex flex-wrap gap-1">
                    {f.options!.slice(0, 18).map((o) => (
                      <span key={(o.code ?? "") + o.label} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-1.5 text-[10px] text-gray-600">
                        {o.hex && <span className="h-2.5 w-2.5 rounded-full border border-gray-300" style={{ background: o.hex }} />}
                        {o.code ? `${o.code} ` : ""}{o.label}
                      </span>
                    ))}
                    {f.options!.length > 18 && <span className="text-[10px] text-gray-400">+{f.options!.length - 18}</span>}
                  </p>
                )}
                {f.pattern && <p className="text-gray-400">Kalıp: {f.pattern}</p>}
                {f.maxChar && <p className="text-gray-400">En fazla {f.maxChar} karakter</p>}
              </div>
            ))}
            <div className="rounded-lg border border-gray-200 bg-white p-2">
              <p className="font-medium text-gray-800">Görseldeki ürün: {map.mockup.itemCount}</p>
              <p className="mt-0.5 text-gray-600">
                {map.mockup.items.map((it, i) => (
                  <span key={i} className="mr-2">{it.text}{it.productColor ? <span className="text-gray-400"> ({it.productColor})</span> : null}</span>
                ))}
              </p>
              {map.fixedText.length > 0 && <p className="text-gray-400">Sabit yazı: {map.fixedText.join(", ")}</p>}
            </div>
          </div>

          {/* Renk kartelasi (gorsellerde varsa) */}
          {(map.colorOptions?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-2">
              <p className="mb-1 font-medium text-gray-700">Renk seçenekleri ({map.colorOptions!.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {map.colorOptions!.map((c) => {
                  // Bilinen baski renk adlarinda sabit ton (kartela ile ayni)
                  const hex = inkHex(c.name, c.hex);
                  return (
                    <span key={c.name} className="flex items-center gap-1 rounded-full border border-gray-200 py-0.5 pl-2 pr-1 text-[11px] text-gray-700">
                      <span className="h-3 w-3 rounded-full border border-gray-300" style={{ background: hex ?? "transparent" }} />
                      {c.name}
                      <HexCopy hex={hex} />
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Strateji */}
          <div className="rounded-lg border border-gray-200 bg-white p-2">
            <div className="flex items-center gap-3">
              <span className="font-medium text-gray-700">Strateji:</span>
              <label className="flex items-center gap-1">
                <input type="radio" checked={map.strategy === "replace"} onChange={() => save({ strategy: "replace", chosen: { ...map.chosen, [field.key]: alts[0] ?? map.chosen[field.key] } })} />
                İsimleri değiştir
              </label>
              <label className="flex items-center gap-1" title="Örnek isimler satış getiriyorsa: aynı isimler kalır, hangi üründe hangisinin olduğu değişir">
                <input type="radio" checked={map.strategy === "reorder"} onChange={() => save({ strategy: "reorder", chosen: { ...map.chosen, [field.key]: field.exampleValues[1] ?? field.exampleValues[0] } })} />
                <Shuffle className="h-3 w-3" /> İsimleri koru, yerlerini değiştir
              </label>
            </div>
            {map.strategyReasonTr && <p className="mt-1 text-gray-500">AI önerisi: {map.strategyReasonTr}</p>}
          </div>

          {/* Tasarimda kullanilacak deger */}
          <div>
            <p className="mb-1 font-medium text-gray-700">Tasarımdaki {field.labelTr.toLowerCase()} (1 tasarım):</p>
            <div className="flex flex-wrap gap-1.5">
              {(field.options?.length ? field.options.map((o) => o.label) : map.strategy === "reorder" ? field.exampleValues : alts).map((v) => (
                <button key={v} onClick={() => save({ chosen: { ...map.chosen, [field.key]: v } })}
                  className={`rounded-full border px-2.5 py-0.5 ${map.chosen[field.key] === v ? "border-fuchsia-500 bg-fuchsia-600 text-white" : "border-gray-200 bg-white text-gray-700 hover:border-fuchsia-300"}`}>
                  {v}
                </button>
              ))}
              {map.strategy === "replace" && !field.options?.length && (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const v = newAlt.trim(); if (!v) return;
                  save({ alternatives: { ...map.alternatives, [field.key]: [v, ...alts.filter((x) => x !== v)] }, chosen: { ...map.chosen, [field.key]: v } });
                  setNewAlt("");
                }}>
                  <input value={newAlt} onChange={(e) => setNewAlt(e.target.value)} placeholder="+ kendi değerin"
                    className="w-32 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5" />
                </form>
              )}
            </div>
          </div>

          {/* Secenekli urun: birincil alan disindaki secimler (serit rengi vb.) */}
          {map.fields.filter((f) => f.key !== field.key && isChoice(f) && f.options?.length).map((f) => (
            <div key={f.key} className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-700">Tasarımdaki {f.labelTr.toLowerCase()}:</span>
              <select value={map.chosen[f.key] ?? ""} onChange={(e) => save({ chosen: { ...map.chosen, [f.key]: e.target.value } })}
                className="rounded border border-gray-200 bg-white px-2 py-1">
                {f.options!.map((o) => <option key={(o.code ?? "") + o.label} value={o.label}>{o.code ? `${o.code} ` : ""}{o.label}</option>)}
              </select>
              {(() => { const hex = f.options!.find((o) => o.label === map.chosen[f.key])?.hex; return hex ? <span className="h-4 w-4 rounded border border-gray-300" style={{ background: hex }} /> : null; })()}
            </div>
          ))}

          {groups.length > 1 && (
            <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/40 p-2">
              <p className="mb-1 font-medium text-gray-700">Bu üründe {groups.length} ayrı baskı var — her biri ayrı tasarım olarak üretilir:</p>
              <div className="flex flex-wrap gap-3">
                {groups.map((g) => (
                  <div key={g.id} className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold text-fuchsia-800">{g.label}</span>
                    <span className="text-gray-400">(ilhamda "{g.sample}")</span>
                    {g.keys.map((k) => {
                      const f = map.fields.find((x) => x.key === k)!;
                      return (
                        <input key={k} defaultValue={map.chosen[k] ?? ""} title={f.labelTr} placeholder={f.labelTr}
                          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (map.chosen[k] ?? "")) save({ chosen: { ...map.chosen, [k]: v } }); }}
                          className="w-28 rounded border border-gray-200 bg-white px-2 py-0.5 font-semibold" />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Yazi rengi: uretimden ONCE gorulur/degistirilir */}
          {(() => {
            const detHex = detectedInk(map)?.hex;
            const cur = effectiveInk(map);
            const opts: Array<{ name: string; hex: string; tag?: string }> = [];
            const push = (name: string, hex?: string | null, tag?: string) => {
              if (!hex || !/^#[0-9a-f]{6}$/i.test(hex) || opts.some((o) => o.hex.toLowerCase() === hex.toLowerCase())) return;
              opts.push({ name, hex, tag });
            };
            map.mockup.items.forEach((it) => push(it.textColor || it.textHex || "", it.textHex || (it.textColor ? inkHex(it.textColor, undefined) : null), "ilham"));
            (map.colorOptions ?? []).forEach((c) => push(c.name, inkHex(c.name, c.hex), "kartela"));
            (map.chartColors ?? []).forEach((c) => push(c.name, inkHex(c.name, c.hex), "kartela"));
            const pick = (v: { name: string; hex: string } | null) => void save({ designInk: v });
            return (
              <div className="rounded-lg border border-gray-200 bg-white p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-700">Yazı rengi:</span>
                  <span className="flex items-center gap-1.5 rounded-full border border-fuchsia-300 bg-fuchsia-50 py-0.5 pl-1 pr-2 font-semibold text-fuchsia-800">
                    <span className="h-4 w-4 rounded-full border border-gray-300" style={{ background: cur?.hex ?? "repeating-conic-gradient(#ddd 0 25%, #fff 0 50%) 50% / 8px 8px" }} />
                    {cur ? cur.name : "Referanstaki gibi (AI seçer)"}
                    {cur && <HexCopy hex={cur.hex} />}
                  </span>
                  <label className="flex cursor-pointer items-center gap-1 text-gray-500" title="Özel renk">
                    <input type="color" value={cur?.hex ?? "#ffffff"} onChange={(e) => pick({ name: e.target.value.toUpperCase(), hex: e.target.value })}
                      className="h-6 w-8 cursor-pointer rounded border border-gray-200 p-0" />
                    özel
                  </label>
                  {cur && (
                    <button onClick={() => pick(null)} className="text-gray-400 underline hover:text-gray-600">referanstaki gibi</button>
                  )}
                  {!detHex && <span className="text-amber-600">İlham görselinde yazı rengi algılanamadı — seç ya da analizi yenile.</span>}
                </div>
                {opts.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {opts.map((o) => (
                      <button key={o.hex} onClick={() => pick({ name: o.name, hex: o.hex })} title={`${o.name} ${o.hex}${o.tag ? ` · ${o.tag}` : ""}`}
                        className={`flex items-center gap-1 rounded-full border py-0.5 pl-1 pr-2 text-[11px] ${cur?.hex.toLowerCase() === o.hex.toLowerCase() ? "border-fuchsia-500 bg-fuchsia-50 text-fuchsia-800" : "border-gray-200 text-gray-600 hover:border-fuchsia-300"}`}>
                        <span className="h-3 w-3 rounded-full border border-gray-300" style={{ background: o.hex }} />
                        {o.name}{o.tag === "ilham" && <span className="text-[9px] text-fuchsia-500">★</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {(isOrnament || multiFields.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-fuchsia-100 bg-fuchsia-50/40 px-3 py-2">
              <span className="font-medium text-gray-700">Tasarım ayarları{isOrnament ? " · Ürün: Süsleme (ornament)" : ""}</span>
              {isOrnament && (
                <label className="flex items-center gap-1.5 text-gray-600">Şekil
                  <select value={shapeOpt} aria-label="Ornament şekli"
                    onChange={(e) => { const v = e.target.value as "round" | "heart" | "any"; setShapeOpt(v); remember("erc:custom:shape", v); }}
                    className="rounded border border-gray-300 px-1.5 py-0.5">
                    <option value="round">Yuvarlak</option>
                    <option value="heart">Kalp</option>
                    <option value="any">İkisine de uyan (tek tasarım)</option>
                  </select>
                </label>
              )}
              {isOrnament && (
                <label className="flex items-center gap-1.5 text-gray-600" title={shapeOpt === "any" ? "İkisine uyan tasarım zeminsiz üretilir" : undefined}>Zemin
                  <select value={shapeOpt === "any" ? "transparent" : bgOpt} disabled={shapeOpt === "any"} aria-label="Ornament zemini"
                    onChange={(e) => { const v = e.target.value as "transparent" | "full"; setBgOpt(v); remember("erc:custom:bg", v); }}
                    className="rounded border border-gray-300 px-1.5 py-0.5 disabled:opacity-60">
                    <option value="transparent">Zeminsiz (beyaz seramik görünür)</option>
                    <option value="full">Kenara kadar dolu renkli zemin</option>
                  </select>
                </label>
              )}
              {multiFields.map((mf) => (
                <label key={mf.key} className="flex items-center gap-1.5 text-gray-600" title={`İlhamda ${mf.sample} isim var; örnek tasarım kaç isimle yapılsın`}>
                  {mf.label}: isim sayısı
                  <input type="number" min={1} max={12} value={nameCounts[mf.key] ?? mf.sample}
                    onChange={(e) => setNameCounts((c) => ({ ...c, [mf.key]: Math.max(1, Math.min(12, Number(e.target.value) || mf.sample)) }))}
                    aria-label={`${mf.label} isim sayısı`} className="w-14 rounded border border-gray-300 px-1.5 py-0.5" />
                </label>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {modelSelect}
            {groups.length > 1 && groups.map((g) => (
              <button key={g.id}
                onClick={() => void run("design", async () => {
                  await api(`/${analysisId}/customization/design`, "POST", { model, ink: effectiveInk(map), ...groupArgs(g.keys), ...designOpts() });
                  void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
                })}
                disabled={busy !== null || !groupValue(map, g.keys)}
                title={`Yalnızca bu baskı üretilir (ilhamdaki: "${g.sample}")`}
                className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
              >
                {busy === "design" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                {g.label}: "{groupValue(map, g.keys)}" tasarımı üret
              </button>
            ))}
            {groups.length <= 1 && <button
              onClick={() => void run("design", async () => {
                await api(`/${analysisId}/customization/design`, "POST", { model, ink: effectiveInk(map), ...designOpts() });
                void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
              })}
              disabled={busy !== null || !map.chosen[field.key]}
              className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50"
            >
              {busy === "design" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              "{map.chosen[field.key]}" ile tasarım üret
            </button>}
            <button onClick={() => setUpload(upload ? null : { value: map.chosen[field.key] ?? "" })}
              title="Hazır tasarımını (Canva vb.) yükle — hangi isme ait olduğunu belirt"
              className="flex items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-1.5 font-medium text-fuchsia-700 hover:bg-fuchsia-50">
              <Upload className="h-3.5 w-3.5" /> Dışarıdan tasarım ekle
            </button>
            <span className="text-gray-400">Tasarım arka planda üretilir; aşağıda ve Tasarımlar sayfasında görünür.</span>
          </div>
          {upload && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-fuchsia-200 bg-white p-2">
              <label className="flex items-center gap-1">
                Tasarımdaki {field.labelTr.toLowerCase()}:
                <input value={upload.value} onChange={(e) => setUpload({ value: e.target.value })}
                  placeholder={field.exampleValues[0] ?? "örn. Wildcats"}
                  className="w-40 rounded border border-gray-200 px-2 py-1 font-semibold" />
              </label>
              <input ref={uploadRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { uploadDesign(e.target.files?.[0]); e.target.value = ""; }} />
              <button onClick={() => uploadRef.current?.click()} disabled={!upload.value.trim() || busy !== null}
                className="flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1 font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                {busy === "design" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Dosya seç ve ekle
              </button>
              <span className="text-gray-400">İsim, kolajda diğer ürünlerin yazısını değiştirmek için kullanılır.</span>
              <button onClick={() => setUpload(null)} className="ml-auto text-gray-400 hover:text-gray-700">Vazgeç</button>
            </div>
          )}

          {/* Custom tasarimlar + kolaj */}
          {customDesigns.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-2">
              <p className="mb-1.5 font-medium text-gray-700">
                Üretilen tasarımlar — tıkla: düzenle · yazıyı kontrol et, yanlışsa yeniden üret · kolajda kullanılacağı seç:
              </p>
              <div className="flex flex-wrap gap-3">
                {customDesigns.map((d) => {
                  const values = d.prompt?.match(/^CUSTOM \(([^)]*)\)/)?.[1] ?? "";
                  const chosenJson = d.prompt?.match(/^CUSTOM \([^)]*\) (\{[^}]*\})/)?.[1];
                  let chosen: Record<string, string> | undefined;
                  try { chosen = chosenJson ? JSON.parse(chosenJson) : undefined; } catch { chosen = undefined; }
                  if (!chosen && field && values) chosen = { [field.key]: values.split(", ")[0] };
                  return (
                    <div key={d.id} className="w-36">
                      <button onClick={() => d.imageUrl && void openDetail(d.id)} disabled={!d.imageUrl}
                        title="Tıkla: tasarımı düzenle (varyant, Canva, arka plan…)"
                        className={`relative block h-36 w-36 overflow-hidden rounded border-2 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:16px_16px] ${designId === d.id ? "border-fuchsia-500" : "border-gray-200"}`}>
                        {d.imageUrl
                          ? <img src={abs(d.imageUrl)} alt="" className="h-full w-full object-contain" />
                          : <span className="flex h-full items-center justify-center text-[10px] text-gray-400">
                              {d.status === "generating" ? <Loader2 className="h-5 w-5 animate-spin" /> : (d.error ? "hata" : "…")}
                            </span>}
                      </button>
                      {d.imageUrl && (
                        <label className="mt-1 flex cursor-pointer items-center gap-1 text-[11px] text-gray-700">
                          <input type="radio" name="collage-design" checked={designId === d.id} onChange={() => setDesignId(d.id)} />
                          Kolajda kullan
                        </label>
                      )}
                      <p className="mt-1 text-[11px] text-gray-500">İstenen yazı:</p>
                      <p className="truncate text-sm font-bold tracking-wide text-gray-900" title={values}>{values || "—"}</p>
                      <p className="truncate text-[10px] text-gray-400" title={d.model ?? ""}>Model: {modelName(d.model)}</p>
                      {d.imageUrl && (
                        <button onClick={() => setTplEdit(d.id)}
                          title="Yazıyı gerçek fontla düzenlenebilir katmana çevir: isim varyantları, kolaj ve siparişler AI'sız basılır"
                          className={`mt-1 w-full rounded border px-1.5 py-0.5 text-[11px] font-medium ${map.textTemplates?.[d.id] ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"}`}>
                          {map.textTemplates?.[d.id] ? "✓ Şablonu düzenle" : "Düzenlenebilir şablon"}
                        </button>
                      )}
                      {d.imageUrl && (
                        <div className="mt-1 flex items-center gap-1.5">
                          {variants[d.id]?.variantUrl ? (
                            <button onClick={() => setZoom(abs(variants[d.id].variantUrl))} title="Koyu tişört varyantı — büyüt"
                              className="h-9 w-9 shrink-0 overflow-hidden rounded border border-gray-300 bg-gray-800">
                              <img src={abs(variants[d.id].variantUrl)} alt="" className="h-full w-full object-contain" />
                            </button>
                          ) : null}
                          <button
                            onClick={() => void run("design", async () => {
                              await api(`/${analysisId}/customization/dark-variant`, "POST", { analysisDesignId: d.id, model });
                              await refetchVariants();
                              setInfo("Koyu varyant hazır — kolajda koyu tişörtlere, listelerde koyu mockuplara basılır.");
                            })}
                            disabled={busy !== null}
                            title="İlham görselinde koyu tişörtlerde renkler nasıl değişiyorsa, tasarımın koyu tişört versiyonunu üretir"
                            className="rounded border border-gray-300 bg-gray-800 px-1.5 py-0.5 text-[10px] text-white hover:bg-gray-700 disabled:opacity-50"
                          >
                            {variants[d.id]?.variantUrl ? "Koyu varyantı yenile" : "Koyu varyant üret"}
                          </button>
                          <label title="Koyu varyantı dosyadan yükle"
                            className="cursor-pointer rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50">
                            Yükle
                            <input type="file" accept="image/*" className="hidden"
                              onChange={(e) => { uploadDark(d.id, e.target.files?.[0]); e.target.value = ""; }} />
                          </label>
                        </div>
                      )}
                      {revise?.kind === "design" && revise.key === d.id && (
                        <div className="mt-1 space-y-1">
                          <textarea autoFocus rows={3} value={revise.prompt}
                            onChange={(e) => setRevise({ ...revise, prompt: e.target.value })}
                            placeholder="Ek talimat (örn. yazıyı daha kalın yap, kurdeleyi sola al)"
                            className="w-full rounded border border-indigo-200 px-1.5 py-1 text-[11px]" />
                          <button
                            onClick={() => void run("design", async () => {
                              await api(`/${analysisId}/customization/design`, "POST", { model, chosen, extraPrompt: revise.prompt, previousDesignId: d.id, ink: effectiveInk(map), ...(chosen ? groupArgs(Object.keys(chosen)) : {}), ...designOpts() });
                              setRevise(null);
                              void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
                            })}
                            disabled={busy !== null}
                            className="w-full rounded bg-indigo-600 px-1.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            Revize gönder
                          </button>
                        </div>
                      )}
                      {d.status !== "generating" && (
                        <div className="mt-1 flex gap-1">
                          <button
                            onClick={() => void run("design", async () => {
                              await api(`/${analysisId}/customization/design`, "POST", { model, chosen, ink: effectiveInk(map), ...(chosen ? groupArgs(Object.keys(chosen)) : {}), ...designOpts() });
                              void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
                            })}
                            disabled={busy !== null}
                            title={`Yazı yanlışsa: aynı isimle, seçili modelle (${modelName(model)}) yeni tasarım üretir`}
                            className="flex-1 rounded border border-fuchsia-300 px-1.5 py-0.5 text-[11px] text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50"
                          >
                            Yanlışsa yeniden üret
                          </button>
                          <button
                            onClick={() => setRevise(revise?.kind === "design" && revise.key === d.id ? null : { kind: "design", key: d.id, prompt: "" })}
                            title="Aynı isimle, bu tasarımı referans alarak ek talimatla yeniden üret"
                            className="rounded border border-indigo-200 px-1.5 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-50"
                          >
                            Revize
                          </button>
                          <button
                            onClick={() => {
                              if (!confirm("Bu tasarım silinsin mi?")) return;
                              void apiClient.deleteAnalysisDesign(analysisId, d.id)
                                .then(() => qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] }));
                              if (designId === d.id) setDesignId("");
                            }}
                            title="Sil"
                            className="rounded border border-gray-200 px-1.5 text-gray-500 hover:border-red-300 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {isChoice(field) && designId && !map.textTemplates?.[designId] && (
                <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-amber-900">
                  Bu ürün menüden seçmeli: ürün başına farklı {field.labelTr.toLowerCase()} / renk, kolajda kullanılan tasarımın
                  <b> bileşen şablonundan</b> basılır. Önce tasarımın altındaki <b>"Düzenlenebilir şablon"</b> ile şablonu çıkarın ve eksik görselleri üretin
                  — şablon yoksa tüm ürünlere aynı tasarım basılır.
                </p>
              )}
              {/* Uretilecek kolajin detaylari: tasarim yeniden cizilmez, yalnizca yazi degisir */}
              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-2">
                <p className="mb-1.5 font-medium text-gray-700">Üretilecek kolaj mockup</p>
                <div className="flex gap-3">
                  {selectedDesign?.imageUrl && (
                    <div className="shrink-0">
                      <img src={abs(selectedDesign.imageUrl)} alt=""
                        className="h-24 w-24 rounded border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:12px_12px] object-contain" />
                      <p className="mt-0.5 w-24 text-center text-[10px] text-gray-500">Kullanılacak tasarım</p>
                    </div>
                  )}
                  <div className="min-w-0 flex-1 space-y-1 text-[11px]">
                    <div className="flex items-start gap-2 rounded border border-gray-200 bg-white p-1.5">
                      {map.blankScene ? (
                        <button onClick={() => setZoom(abs(map.blankScene!.url))} title="Büyüt" className="shrink-0">
                          <img src={abs(map.blankScene.url)} alt="" className="h-16 w-auto rounded border border-gray-200 object-contain" />
                        </button>
                      ) : null}
                      <div className="text-gray-600">
                        <p>
                          <b>Boş sahne</b>: ilham fotoğrafının baskısız hali — <b>Mockup Yönetimi'ne mockup olarak</b> eklenir, her ürünün baskı alanı otomatik belirlenir
                          (yanlışsa oradan düzelt). Baskı alanları ilham görselindeki baskılardan ölçülür; tasarım bu alanlara <b>birebir</b> basılır, son adımda AI kişisel yazıyı değiştirip baskıları ilham görselindeki gibi oturtur (örtüşme, kıvrım).
                          {map.blankScene ? "" : " İlk kolajda üretilir (+1 görsel üretimi)."}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-3 text-gray-500">
                          <button onClick={() => setPickBlank(true)} className="font-medium text-indigo-600 hover:underline">Mockuplardan seç</button>
                          {map.blankScene && (
                            <label className="flex items-center gap-1" title="Yalnızca boş sahne bozuksa (tişört rengi/kesimi değişmiş, eski baskı izi var)">
                              <input type="checkbox" checked={regenerateBlank} onChange={(e) => setRegenerateBlank(e.target.checked)} />
                              Boş sahneyi AI ile yeniden üret
                            </label>
                          )}
                          {map.blankScene && (
                            <label className="flex items-center gap-1" title="Baskılar yanlış yerde/boyutta çıktıysa: alanlar ilham görselindeki baskılardan yeniden ölçülür">
                              <input type="checkbox" checked={redetectAreas} onChange={(e) => setRedetectAreas(e.target.checked)} />
                              Baskı alanlarını yeniden ölç
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-500">
                      Sahne: ilham listesinin ilk görseli{map.mockup.sceneTr ? ` — ${map.mockup.sceneTr}` : ""}.
                      Fotoğraf <b>aynen korunur</b> (ürün renkleri, kesim, kıvrımlar, zemin, dizilim); yalnızca ürünlerdeki baskı bizim tasarımla değişir. Tasarım yeniden çizilmez, sadece yazı{plan.some((p) => p.textHex) ? " ve yazı rengi" : ""} değişir.
                    </p>
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-gray-400">
                          <th className="w-14 font-normal">
                            <button onClick={rotatePlan} title="Tasarımları (isim + renk) ürünlere bir kaydırarak yeniden dağıt"
                              className="rounded border border-gray-200 bg-white px-1 text-[10px] text-gray-600 hover:bg-gray-50">
                              <Shuffle className="inline h-3 w-3" /> Kaydır
                            </button>
                          </th>
                          <th className="font-normal">Ürün rengi</th>
                          <th className="font-normal">Üründe yazacak</th>
                          <th className="font-normal" title="Satıra atanmış hazır tasarım. Boşsa isim varyantı otomatik üretilir.">Tasarım</th>
                          <th className="font-normal" title="Tek renkli tasarımda: tasarım bu renge boyanıp basılır. Boş = tasarımın kendi rengi">Yazı rengi</th>
                          <th className="w-24 text-right font-normal">
                            <button
                              onClick={() => {
                                const todo = plan.map((r, i) => (r.value.trim() && !r.designId ? i : -1)).filter((i) => i >= 0);
                                if (todo.length === 0) { setInfo("Tüm satırlara tasarım atanmış — üretilecek bir şey yok."); return; }
                                if (!confirm(`${todo.length} satır için tasarım üretilecek (~$0.04–0.07/adet, daha önce üretilen isimler tekrar üretilmez). Devam?`)) return;
                                for (const i of todo) void produceItem(i);
                              }}
                              disabled={!designId || itemBusy.size > 0}
                              title="Tasarım atanmamış satırların tasarımını ayrı tasarım olarak üret (Tasarımlar sayfasına düşer)"
                              className="rounded border border-fuchsia-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50"
                            >
                              Hepsini üret
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.map((it, i) => (
                          <tr key={i}>
                            <td className="whitespace-nowrap text-gray-400">
                              {i + 1}
                              <button onClick={() => swapRows(i, i - 1)} disabled={i === 0} title="Bu tasarımı bir üstteki ürüne taşı"
                                className="ml-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">↑</button>
                              <button onClick={() => swapRows(i, i + 1)} disabled={i === plan.length - 1} title="Bu tasarımı bir alttaki ürüne taşı"
                                className="text-gray-400 hover:text-gray-700 disabled:opacity-30">↓</button>
                            </td>
                            <td className="pr-2">
                              {/* Urun rengi ilham fotografindan gelir, DEGISMEZ (sadece baski degisir) */}
                              <span className="text-gray-600">{it.productColor || "—"}</span>
                            </td>
                            <td>
                              {field.options?.length ? (
                                <select value={it.value}
                                  onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value, designId: undefined } : x)))}
                                  className="w-full rounded border border-gray-200 px-1.5 py-0.5 font-semibold">
                                  {!field.options.some((o) => o.label === it.value) && <option value={it.value}>{it.value}</option>}
                                  {field.options.map((o) => <option key={(o.code ?? "") + o.label} value={o.label}>{o.code ? `${o.code} ` : ""}{o.label}</option>)}
                                </select>
                              ) : (
                                <input value={it.value}
                                  // Isim degisince eski tasarim atamasi duser: yoksa kolaj eski ismi basiyordu
                                  onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value, designId: undefined } : x)))}
                                  className="w-full rounded border border-gray-200 px-1.5 py-0.5 font-semibold" />
                              )}
                              {map.fields.filter((f) => f.key !== field.key && isChoice(f) && f.options?.length).map((f) => {
                                const cur = it.values?.[f.key] ?? "";
                                const hex = f.options!.find((o) => o.label === cur)?.hex;
                                return (
                                  <label key={f.key} className="mt-0.5 flex items-center gap-1 text-[10px] text-gray-500">
                                    <span className="shrink-0">{f.labelTr}:</span>
                                    {hex && <span className="h-3 w-3 shrink-0 rounded-full border border-gray-300" style={{ background: hex }} />}
                                    <select value={cur}
                                      onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, values: { ...(x.values ?? {}), [f.key]: e.target.value }, designId: undefined } : x)))}
                                      className="min-w-0 flex-1 rounded border border-gray-200 px-1 py-0">
                                      <option value="">—</option>
                                      {f.options!.map((o) => <option key={(o.code ?? "") + o.label} value={o.label}>{o.code ? `${o.code} ` : ""}{o.label}</option>)}
                                    </select>
                                  </label>
                                );
                              })}
                              {rowIssues(map, field.key, it).map((iss, k) => (
                                <p key={k} className={`mt-0.5 text-[10px] ${iss.level === "error" ? "text-red-600" : "text-amber-700"}`}>⚠ {iss.message}</p>
                              ))}
                            </td>
                            <td className="pl-2">
                              <div className="flex items-center gap-1">
                                {(() => {
                                  const d = customDesigns.find((x) => x.id === it.designId);
                                  return d?.imageUrl ? (
                                    <img src={abs(d.imageUrl)} alt="" title={d.sku ?? ""}
                                      className="h-7 w-7 shrink-0 rounded border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:8px_8px] object-contain" />
                                  ) : <span className="h-7 w-7 shrink-0 rounded border border-dashed border-gray-300" title="Atanmadı — isim varyantı üretilir" />;
                                })()}
                                <select value={it.designId ?? ""}
                                  onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, designId: e.target.value || undefined } : x)))}
                                  className="w-28 rounded border border-gray-200 px-1 py-0.5 text-[10px]">
                                  <option value="">Otomatik üret</option>
                                  {customDesigns.filter((d) => d.status === "completed" && d.imageUrl).map((d) => (
                                    <option key={d.id} value={d.id}>{valueOfDesign(d) || "?"} · {d.sku ?? ""}</option>
                                  ))}
                                </select>
                                <button onClick={() => { setRowUploadIdx(i); rowUploadRef.current?.click(); }}
                                  disabled={itemBusy.has(i) || !it.value.trim()}
                                  title="Bu satırın tasarımını dosyadan yükle (satırın ismiyle kaydedilir ve atanır)"
                                  className="rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                  Yükle
                                </button>
                              </div>
                            </td>
                            <td className="pl-2">
                              <div className="flex items-center gap-1">
                                <input type="color" value={it.textHex || "#000000"}
                                  onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, textHex: e.target.value } : x)))}
                                  className={`h-5 w-6 cursor-pointer ${it.textHex ? "" : "opacity-30"}`} />
                                <input value={it.textColor} placeholder="kendi rengi"
                                  onChange={(e) => setPlan((p) => p.map((x, j) => (j === i ? { ...x, textColor: e.target.value } : x)))}
                                  className="w-24 rounded border border-gray-200 px-1.5 py-0.5" />
                                <HexCopy hex={it.textHex || null} />
                                {it.textHex && (
                                  <button onClick={() => setPlan((p) => p.map((x, j) => (j === i ? { ...x, textHex: "", textColor: "" } : x)))}
                                    title="Boya kaldır: tasarım kendi renginde basılır" className="text-gray-400 hover:text-red-600">×</button>
                                )}
                              </div>
                            </td>
                            <td className="pl-2 text-right">
                              <button onClick={() => void produceItem(i)} disabled={!designId || itemBusy.has(i) || !it.value.trim()}
                                title="Bu ürünün tasarımını (isim + baskı rengi) ayrı tasarım olarak üret"
                                className="inline-flex items-center gap-1 rounded bg-fuchsia-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50">
                                {itemBusy.has(i) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} Üret
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <input ref={rowUploadRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => { uploadForRow(e.target.files?.[0]); e.target.value = ""; }} />
                    {(() => {
                      const rf = ruleFields(map, plan.map((p) => p.productColor ?? ""), plan.map((p) => p.textColor ?? ""));
                      return rf.length >= 2 ? (
                        <OptionRulesEditor fields={rf} rules={map.optionRules ?? []} onChange={(optionRules) => save({ optionRules })} />
                      ) : null;
                    })()}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {modelSelect}
                  <button
                    onClick={() => confirmGenerations("Önizleme") && void run("mockup", async () => {
                      const r = await api<{ previewUrl: string; blankScene?: CustomizationMap["blankScene"] }>(
                        `/${analysisId}/customization/mockup`, "POST",
                        { model, analysisDesignId: designId, items: plan, regenerateBlank, redetectAreas, previewOnly: true });
                      setPreviewUrl(r.previewUrl);
                      setMap((m) => (m ? { ...m, blankScene: r.blankScene ?? m.blankScene } : m));
                      setRegenerateBlank(false); setRedetectAreas(false);
                    })}
                    disabled={busy !== null || !designId}
                    title="Her ürüne kendi ismi ve rengiyle kodla basılmış hali. İlk seferde her isim için tasarım varyantı üretilir (saklanır, sonra tekrar kullanılır); boş sahne yoksa o da üretilir"
                    className="flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                  >
                    Yerleşimi önizle
                  </button>
                  <button onClick={() => confirmGenerations("Mockup'a yerleştir") && setPickPlaceMockup(true)} disabled={busy !== null || !designId}
                    title="Çok alanlı bir mockup seç: her ürünün tasarımı alanlarına sırayla kodla yerleştirilir. Tüm satırlara tasarım atanmışsa HİÇ AI kullanılmaz"
                    className="rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                    Mockup'a yerleştir
                  </button>
                  {map.blankScene?.mockupId && (
                    <button onClick={() => void openAreaEditor()} disabled={busy !== null}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      Alanları düzenle
                    </button>
                  )}
                  <select value={finalStep} onChange={(e) => setFinalStep(e.target.value as "ai" | "none")}
                    title="Son adım: AI baskıları kumaşa oturtur (kıvrım, örtüşme). 'Yok' seçilirse sonuç önizlemeyle birebir aynıdır."
                    className="rounded border border-gray-200 bg-white px-2 py-1.5 text-[11px]">
                    <option value="ai">Son adım: AI ile kumaşa oturt</option>
                    <option value="none">Son adım: yok (önizlemeyle birebir)</option>
                  </select>
                  <button
                    onClick={() => confirmGenerations("Kolaj") && void run("mockup", async () => {
                      const c = await api<NonNullable<CustomizationMap["collages"]>[number] & { blankScene?: CustomizationMap["blankScene"]; warning?: string }>(
                        `/${analysisId}/customization/mockup`, "POST", { model, analysisDesignId: designId, items: plan, regenerateBlank, redetectAreas, finalStep });
                      const { blankScene, warning, ...collage } = c;
                      setInfo(warning ?? null);
                      setMap((m) => (m ? { ...m, blankScene: blankScene ?? m.blankScene, collages: [collage, ...(m.collages ?? [])] } : m));
                      setRegenerateBlank(false); setRedetectAreas(false);
                    })}
                    disabled={busy !== null || !designId || plan.every((p) => !p.value.trim())}
                    className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {busy === "mockup" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Kolaj mockup üret ({plan.filter((p) => p.value.trim()).length} ürün)
                  </button>
                </div>
                {previewUrl && (
                  <div className="mt-2">
                    <p className="mb-1 text-gray-500">
                      Yerleşim önizlemesi — her ürün kendi ismi ve rengiyle. İsim/renk dağılımı burada kesinleşir; son adım (seçiliyse) yalnızca kumaşa oturtur. Yanlış yer varsa <b>Alanları düzenle</b>.
                    </p>
                    <button onClick={() => setZoom(abs(previewUrl))} title="Büyüt">
                      <img src={abs(previewUrl)} alt="" className="max-h-72 w-auto rounded border border-gray-200 bg-white object-contain" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Renk kartelasi: gorsellerde kartela varsa, secili tasarim her renge boyanir */}
          {/* Kartela DOGRUDAN cizilmez: once oneri, kullanici acarsa kurulur */}
          {((map.colorOptions?.length ?? 0) > 0 || chartDesigns.length > 1) && selectedDesign?.imageUrl && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2">
              <p className="text-gray-700">
                <b>Öneri:</b> {(map.colorOptions?.length ?? 0) > 0
                  ? <>ilham listesinde {map.colorOptions!.length} renkli bir baskı rengi kartelası var. Seçili tasarımla</>
                  : <>{chartDesigns.length} tasarımınla</>}
                {" "}mağaza stilinde kendi kartelanı oluşturabilirsin{chartDesigns.length > 1 ? " (oluşturulan tasarımlardan da)" : ""} — kaynağın zemini ve başlığı kullanılmaz.
              </p>
              <button onClick={() => setChartOpen(true)}
                className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700">
                Kartela oluştur
              </button>
            </div>
          )}
          {((map.colorOptions?.length ?? 0) > 0 || chartDesigns.length > 1) && selectedDesign?.imageUrl && chartOpen && (
            <ColorChartBuilder
              designUrl={abs(selectedDesign.imageUrl)}
              colors={map.colorOptions ?? []}
              designs={chartDesigns}
              matrixColors={map.chartColors?.length ? map.chartColors : (() => {
                // Tasarimlarin baski renkleri (plan satirlari) — tekil
                const seen = new Set<string>();
                return plan.filter((r) => r.textHex && !seen.has(r.textHex) && seen.add(r.textHex))
                  .map((r) => ({ name: (r.textColor || r.textHex).toUpperCase(), hex: r.textHex }));
              })()}
              onMatrixColorsChange={(c) => save({ chartColors: c })}
              referenceImages={map.imageUrls ?? []}
              saving={chartSaving}
              onClose={() => setChartOpen(false)}
              onSave={(dataUrls) => {
                setChartSaving(true); setError(null);
                void (async () => {
                  try {
                    const saved: NonNullable<CustomizationMap["colorCharts"]> = [];
                    for (const image of dataUrls) {
                      saved.push(await api<NonNullable<CustomizationMap["colorCharts"]>[number]>(`/${analysisId}/customization/chart`, "POST", { image, analysisDesignId: designId }));
                    }
                    setMap((m) => (m ? { ...m, colorCharts: [...saved, ...(m.colorCharts ?? [])] } : m));
                    setChartOpen(false);
                    setInfo(`${saved.length} kartela görseli kaydedildi — kütüphanede ve listenin ek görsellerinde.`);
                  } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
                  finally { setChartSaving(false); }
                })();
              }}
            />
          )}
          {(map.colorCharts?.length ?? 0) > 0 && (
            <div>
              <p className="mb-1 font-medium text-gray-700">Renk kartelaları (en yenisi listede kolaj + satan mockuptan sonra):</p>
              <div className="flex flex-wrap gap-2">
                {map.colorCharts!.map((c) => (
                  <button key={c.url} onClick={() => setZoom(abs(c.url))} className="overflow-hidden rounded border border-gray-200 bg-white">
                    <img src={abs(c.url)} alt="" className="h-32 w-auto object-contain" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kolaj revizyonu: ayni tasarim + ayni plan + onceki sonuc + ek talimat */}
          {revise?.kind === "collage" && (() => {
            const c = (map.collages ?? []).find((x) => x.url === revise.key);
            if (!c) return null;
            const items = c.items ?? c.values.map((value, i) => ({ value, productColor: map.mockup.items[i]?.productColor }));
            return (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2">
                <p className="mb-1 font-medium text-gray-700">Kolajı revize et</p>
                <div className="flex gap-3">
                  <img src={abs(c.url)} alt="" className="h-40 w-auto shrink-0 rounded border border-gray-200 bg-white object-contain" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="text-gray-500">
                      Aynı veriler tekrar gönderilir: tasarım, ürün sırası ve yazılar ({items.map((it) => it.value).join(" · ")}), boş sahne ve bu kolajın kendisi (neyin yanlış olduğunu görsün diye).
                      {c.extraPrompt ? ` Önceki talimat: "${c.extraPrompt}"` : ""}
                    </p>
                    <textarea autoFocus rows={3} value={revise.prompt}
                      onChange={(e) => setRevise({ ...revise, prompt: e.target.value })}
                      placeholder="Ne düzelsin? örn. çizgiler tasarımdaki gibi pembe-beyaz olsun, tişört kesimleri değişmesin"
                      className="w-full rounded border border-indigo-200 px-2 py-1" />
                    <div className="flex flex-wrap items-center gap-2">
                      {modelSelect}
                      <button
                        onClick={() => void run("mockup", async () => {
                          const r = await api<NonNullable<CustomizationMap["collages"]>[number] & { blankScene?: CustomizationMap["blankScene"] }>(
                            `/${analysisId}/customization/mockup`, "POST",
                            { model, analysisDesignId: c.analysisDesignId, items, extraPrompt: revise.prompt, previousUrl: c.url, regenerateBlank, redetectAreas });
                          const { blankScene, ...collage } = r;
                          setMap((m) => (m ? { ...m, blankScene: blankScene ?? m.blankScene, collages: [collage, ...(m.collages ?? [])] } : m));
                          setRevise(null); setRegenerateBlank(false);
                        })}
                        disabled={busy !== null}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {busy === "mockup" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        Revize gönder
                      </button>
                      <button onClick={() => setRevise(null)} className="text-gray-500 hover:text-gray-700">Vazgeç</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Listeye tasima: tasarim (+acik/koyu) + tum kolajlar + kartela */}
          {designId && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
              <span className="font-medium text-gray-700">Listeye taşı:</span>
              <button onClick={() => void sendTo("review")} disabled={sending !== null}
                title="Seçili tasarım, kolajlar (ana görsel) ve kartela ile Liste Denetimi'ni yeni sekmede açar"
                className="flex items-center gap-1 rounded-lg bg-white px-3 py-1 font-medium text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-50 disabled:opacity-50">
                {sending === "review" && <Loader2 className="h-3 w-3 animate-spin" />} Liste Denetimi'ne gönder
              </button>
              <button onClick={() => void sendTo("studio")} disabled={sending !== null}
                title="Mockup Studio'yu tasarım (açık/koyu) ile açar; kolajlar ve kartela ürün görsellerine eklenir"
                className="flex items-center gap-1 rounded-lg bg-white px-3 py-1 font-medium text-indigo-700 ring-1 ring-indigo-300 hover:bg-indigo-50 disabled:opacity-50">
                {sending === "studio" && <Loader2 className="h-3 w-3 animate-spin" />} Studio'da aç
              </button>
              <button onClick={() => void sendTo("draft")} disabled={sending !== null}
                title="Üretim planındaki şablon ve mockup grubuyla doğrudan taslak oluşturur (kolaj ana görsel)"
                className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1 font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                {sending === "draft" && <Loader2 className="h-3 w-3 animate-spin" />} Doğrudan taslağa çevir
              </button>
              <span className="text-gray-500">Listenin tasarımı: "Kolajda kullan" seçili olan · {(map.collages?.length ?? 0)} kolaj{(map.colorCharts?.length ?? 0) ? " + kartela" : ""} taşınır</span>
            </div>
          )}

          {(map.collages?.length ?? 0) > 0 && (
            <div>
              <p className="mb-1 font-medium text-gray-700">Kolaj mockuplar (en yenisi listede ANA görsel olur):</p>
              <div className="flex flex-wrap gap-2">
                {map.collages!.map((c, i) => (
                  <div key={c.url} className="group/col relative">
                    <button onClick={() => setLightbox(i)} className="overflow-hidden rounded border border-gray-200 bg-white" title={c.values.join(", ")}>
                      <img src={abs(c.url)} alt="" className="h-32 w-auto object-contain" />
                    </button>
                    {i === 0 && <span className="absolute left-1 top-1 rounded bg-fuchsia-600 px-1 text-[9px] font-medium text-white">ana görsel</span>}
                    <button
                      onClick={() => setRevise(revise?.kind === "collage" && revise.key === c.url ? null : { kind: "collage", key: c.url, prompt: "" })}
                      className="mt-1 w-full rounded border border-indigo-200 px-1.5 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-50"
                    >
                      Revize et
                    </button>
                    <button onClick={() => void deleteCollage(c.url)} title="Kolajı sil"
                      className="absolute right-1 top-1 hidden rounded bg-black/55 p-1 text-white group-hover/col:block hover:bg-red-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {detail && (
        <DesignDetailModal
          design={detail}
          showOriginal={showOriginal}
          onToggleOriginal={() => setShowOriginal((v) => !v)}
          onClose={() => { setDetail(null); void refetchVariants(); void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] }); }}
          onDelete={() => { setDetail(null); void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] }); }}
          onRevert={() => setDetail(null)}
          onOpenEditor={() => window.open(`/designs?edit=${detail.id}`, "_blank")}
          onLinkAnalysis={() => window.open(`/designs`, "_blank")}
          onRemoveAnalysis={() => { /* burada analiz baglantisi kaldirilmaz */ }}
          onSellingMockupChange={() => { /* satan mockup tasarim modalinda */ }}
          onUpdate={async (payload) => {
            await apiClient.updateDesign(detail.id, payload);
            setDetail(await apiClient.getDesign(detail.id));
            void refetchVariants();
          }}
          onRefresh={async () => {
            setDetail(await apiClient.getDesign(detail.id));
            void refetchVariants();
          }}
        />
      )}

      {areaEdit && (
        <div className="fixed inset-0 z-[80]">
          <DesignAreaEditor
            mockup={areaEdit}
            backLabel="Kapat"
            testDesignLight={selectedDesign?.imageUrl ? abs(selectedDesign.imageUrl) : null}
            testDesignDark={selectedDesign && variants[selectedDesign.id]?.variantUrl ? abs(variants[selectedDesign.id].variantUrl) : null}
            onCancel={() => setAreaEdit(null)}
            onSave={async (areas: AreaRect[]) => {
              const first = areas[0];
              await fetch(`${getBackendUrl()}/api/mockups/${areaEdit.id}`, {
                method: "PATCH", headers: authHeaders(),
                body: JSON.stringify({
                  designAreas: areas,
                  designAreaX: first ? Math.round(first.x) : 0, designAreaY: first ? Math.round(first.y) : 0,
                  designAreaW: first ? Math.round(first.w) : 0, designAreaH: first ? Math.round(first.h) : 0,
                  designAreaRotation: first ? Math.round(first.rotation || 0) : 0,
                }),
              });
              setAreaEdit(null);
              setInfo("Alanlar kaydedildi — önizlemeyi yenileyip kontrol edin.");
            }}
          />
        </div>
      )}

      {pickPlaceMockup && (
        <MockupChooserModal
          title={`Mockup seç — ${plan.filter((p) => p.value.trim()).length} ürün alanlara sırayla yerleşir`}
          mode="single"
          onPick={(m) => {
            setPickPlaceMockup(false);
            const areaCount = Array.isArray((m as { designAreas?: unknown[] }).designAreas) ? (m as { designAreas: unknown[] }).designAreas.length : 1;
            const n = plan.filter((p) => p.value.trim()).length;
            if (areaCount < n) setInfo(`Bu mockup'ta ${areaCount} alan var; ilk ${areaCount} ürün yerleşir.`);
            void run("mockup", async () => {
              const c = await api<NonNullable<CustomizationMap["collages"]>[number]>(
                `/${analysisId}/customization/mockup`, "POST",
                { model, analysisDesignId: designId, items: plan, mockupId: m.id, finalStep: "none" });
              setMap((mm) => (mm ? { ...mm, collages: [c, ...(mm.collages ?? [])] } : mm));
            });
          }}
          onClose={() => setPickPlaceMockup(false)}
        />
      )}

      {pickBlank && (
        <MockupChooserModal
          title="Boş sahne seç (baskı alanları mockup'tan gelir)"
          mode="single"
          onPick={(m) => {
            setPickBlank(false);
            void api<NonNullable<CustomizationMap["blankScene"]>>(`/${analysisId}/customization/blank-scene`, "POST", { mockupId: m.id })
              .then((b) => setMap((mm) => (mm ? { ...mm, blankScene: b } : mm)))
              .catch((e) => setError(String(e.message ?? e)));
          }}
          onClose={() => setPickBlank(false)}
        />
      )}

      {zoom && (
        <ImageLightbox urls={[zoom]} index={0} onIndex={() => {}} onClose={() => setZoom(null)} />
      )}

      {tplEdit && map && (
        <TextTemplateEditor
          analysisId={analysisId}
          designId={tplEdit}
          fields={map.fields}
          initial={map.textTemplates?.[tplEdit] ?? null}
          chosen={map.chosen}
          model={model}
          onClose={() => setTplEdit(null)}
          onSaved={(t) => setMap((m) => (m ? { ...m, textTemplates: { ...(m.textTemplates ?? {}), [t.analysisDesignId]: t } } : m))}
          onDesignCreated={() => {
            void qc.invalidateQueries({ queryKey: ["analysis-designs", analysisId] });
            void refetchVariants();
          }}
        />
      )}

      {lightbox !== null && map?.collages && (
        <ImageLightbox
          urls={map.collages.map((c) => abs(c.url))}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
          captions={map.collages.map((c) => ({ text: c.values.join(" · ") }))}
          onDelete={(i) => { const u = map.collages![i]?.url; if (u) void deleteCollage(u); setLightbox(null); }}
        />
      )}
    </section>
  );
}
